import { useCallback, useEffect, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { EventEmitter } from 'events';

import { checkAllRestrictions } from '@mssgme/shared';
import { useWorkspace } from './useWorkspace';

// TODO: refactor, use redux, context or useSyncExternalStore instead of EventEmitter
export function createHooks() {
    const ee = new EventEmitter();

    ee.setMaxListeners(100);

    const show = (...args) => ee.emit('show', ...args);
    const hide = () => ee.emit('hide');

    function useWarning() {
        const [state, setState] = useState({ isPresented: false, warning: null });

        useEffect(() => {
            const onShow = (warning) => setState({ isPresented: true, warning });
            const onHide = () => setState((prevState) => ({ ...prevState, isPresented: false }));

            ee.on('show', onShow);
            ee.on('hide', onHide);

            return () => {
                ee.removeListener('show', onShow);
                ee.removeListener('hide', onHide);
            };
        }, []);

        return {
            ...state,
            show,
            hide,
        };
    }

    function useRestriction(rule, subject) {
        const workspace = useWorkspace();
        const [warning, setWarning] = useState();

        const checkRule = useCallback(
            (rule, subject) => checkAllRestrictions(rule, { workspace }, subject, setWarning),
            [workspace]
        );
        const showWarning = useCallback((w = warning) => show(w), [warning]);
        const clearWarning = useCallback(() => setWarning(null), []);
        const handleBlockedEvent = useCallback(
            (e) => {
                if (e?.preventDefault) {
                    e.preventDefault();
                    e.stopPropagation();
                }

                show(warning);
            },
            [warning]
        );

        useDeepCompareEffect(() => {
            if (rule) {
                checkRule(rule, subject);
            }
        }, [checkRule, rule, subject]);

        return {
            warning,
            hasWarning: !!warning,
            showWarning,
            clearWarning,
            hideWarning: hide,
            handleBlockedEvent: warning ? handleBlockedEvent : undefined,
            checkRule,
        };
    }

    return { useWarning, useRestriction };
}

const { useWarning, useRestriction } = createHooks();

export { useWarning, useRestriction };
